import React from "react";
import { Outlet } from "react-router-dom";
import { useAuth } from "./Authprovider";
import { Loader, getSellerNavigationSections, getFirstGrantedAdminPath } from "@b2b/ui-components";
import AppLayout from "@b2b/ui-components/components/Layouts/AppLayout";
import { SellerStatusGuard } from "./SellerStatusGuard";
import { Navigate, useLocation } from "react-router-dom";
import { SellerStatus } from "@b2b/services";
import { useChatUnreadHeader } from "../chat/ChatUnreadHeaderProvider";
import { useNotificationHeader } from "../notifications/NotificationHeaderProvider";
import { BizcoverIndiaFigmaHeader } from "../components/marketing/BizcoverIndiaFigmaHeader";
import { BizcoverIndiaFigmaFooter } from "../components/marketing/BizcoverIndiaFigmaFooter";

/**
 * Combined layout for seller routes that includes both role-based and status-based guards
 */
const PROFILE_COMPLETION_THRESHOLD = 80;

const SellerPrivateLayout: React.FC = () => {
  const {
    isAuthenticated,
    isLoading,
    isSessionProfileLoading,
    userStatus,
    userRole,
    sessionEmail,
    logout,
    sellerStatus,
    profileCompleteWithoutSocial,
    profileCompletionPercentage,
    hasMembershipPlanSelected,
    storefrontComplete,
    allowedModules,
  } = useAuth();
  const location = useLocation();
  const chatUnread = useChatUnreadHeader();
  const notifications = useNotificationHeader();
  const isProfileRoute = /\/(profile|profile_settings|userprofile)(\/|$)/.test(location.pathname);
  const isDashboardShellPath = location.pathname.startsWith("/seller");

  const sellerApproved = sellerStatus === SellerStatus.APPROVED;
  const profileCompleteEnough =
    (typeof profileCompletionPercentage === "number" && profileCompletionPercentage >= PROFILE_COMPLETION_THRESHOLD) ||
    profileCompleteWithoutSocial === true;
  const sellerNavigationItems = getSellerNavigationSections(
    sellerApproved,
    profileCompleteEnough,
    hasMembershipPlanSelected ?? false,
  );

  if (isLoading || (isAuthenticated && isSessionProfileLoading)) {
    return <Loader size={40} color="#f97316" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (userStatus === "not_registered" || userStatus === "pending_registration") {
    return (
      <Navigate
        to="/login?tab=signup"
        replace
        state={{
          fromOAuth: true,
          prefilledEmail: sessionEmail,
        }}
      />
    );
  }

  // Role-based access control
  if (userRole === "admin" && allowedModules !== undefined) {
    return <Navigate to={getFirstGrantedAdminPath(allowedModules)} replace />;
  }

  if (userRole && userRole !== "seller" && userRole !== "admin") {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <AppLayout
      role="seller"
      userRole={userRole}
      onLogout={logout}
      chatUnread={chatUnread}
      notifications={notifications}
      notificationUnreadCount={notifications?.unreadCount ?? 0}
      sellerNavigationItems={sellerNavigationItems}
      profileCompletionPercentage={profileCompletionPercentage}
      sellerApproved={sellerApproved}
      profileCompleteEnough={profileCompleteEnough}
      hasMembershipPlanSelected={hasMembershipPlanSelected ?? false}
      storefrontComplete={storefrontComplete ?? false}
      hideShellHeader={isProfileRoute}
      hideShellFooter={isDashboardShellPath}
      customShellHeader={<BizcoverIndiaFigmaHeader />}
      customShellFooter={<BizcoverIndiaFigmaFooter />}
    >
      <SellerStatusGuard>
        <Outlet />
      </SellerStatusGuard>
    </AppLayout>
  );
};

export default SellerPrivateLayout;

